import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetencyCrumbDocument,
  CompetenciesCompetencyLevelDeleteModalDocument,
  CompetencyLevelsDataCollectionDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type Props = {
  level: {
    id: string
    name: string
  }
  onClose: () => void
  onSuccess?: () => void
}

export const CompetencyLevelDeleteModal = ({ level, onClose, onSuccess }: Props) => {
  const [confirmationName, setConfirmationName] = useState('')

  const [deleteLevel, { loading }] = useMutation(CompetenciesCompetencyLevelDeleteModalDocument, {
    extractorFn: (data) => data.competenciesMutations.deleteCompetenciesCompetencyLevel,
    update(cache) {
      cache.evict({
        id: cache.identify({ id: level.id, __typename: 'CompetenciesCompetencyLevel' }),
      })
    },
    refetchQueries: [CompetencyLevelsDataCollectionDocument, CompetenciesCompetencyCrumbDocument],
  })

  const handleSubmit = async () => {
    if (!isValid) {
      return
    }

    const response = await deleteLevel({
      variables: {
        id: level.id,
      },
    })

    const handled = handleResult(response, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })

      onSuccess?.()
      return handled
    }
  }

  const isValid = confirmationName.trim() === level.name.trim()

  return (
    <Modal
      title={i18n.t('competencies_v3.levels.delete.title')}
      description={i18n.t('competencies_v3.levels.delete.description')}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button loading={loading} disabled={!isValid} submit>
            {i18n.t('common.delete')}
          </Button>
        </>
      }
      illustration="delete-cross"
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <Box flexDirection="column" gap="s8">
        <Text.Normal whiteSpace="breakSpaces">
          {i18n.t('competencies_v3.levels.delete.deletion_input', {
            name: level.name,
          })}
        </Text.Normal>
        <TextField onChange={setConfirmationName} value={confirmationName} />
      </Box>
    </Modal>
  )
}
